import { useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { updateHstAccessRequest as ticketUpdate } from '../state';
import { updateHstAccessRequest as recurringTaskUpdate } from '../../recurringTasks/state';
import { HstAccessRequestView } from '../../../types/shared/accessRequestTypes';
import { EditorMode } from '../types/editorTypes';

/**
 * Custom hook that abstracts Redux state selection and dispatch for
 * HST Access editors. Returns the current request data and
 * a mode-aware update function.
 */
export const useHstAccessEditor = (mode: EditorMode) => {
  const dispatch = useAppDispatch();

  const request = useAppSelector(state =>
    mode === 'ticket'
      ? state.ticket.currentTicket?.hstAccessRequest
      : state.recurringTask.currentRecurringTask?.hstAccessRequest
  );

  // Ticket-only values; callers use them for tickets, not recurring task templates.
  const ticketStatus = useAppSelector(state => state.ticket.currentTicket?.workflow?.status);
  // The create form holds an unsaved ticket without an ID; it has nothing to provision yet.
  const ticketPersisted = useAppSelector(state => Boolean(state.ticket.currentTicket?.id));

  const updateRequest = useCallback(
    (update: Partial<HstAccessRequestView>) => {
      if (mode === 'ticket') {
        dispatch(ticketUpdate(update));
      } else {
        dispatch(recurringTaskUpdate(update));
      }
    },
    [dispatch, mode]
  );

  return { request, ticketStatus, ticketPersisted, updateRequest };
};
